import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaComment,
  FaDownload,
  FaExternalLinkAlt,
  FaFileAlt,
  FaPaperPlane,
  FaRedo,
  FaShippingFast,
} from "react-icons/fa";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "./AuthContext";
import {
  CUSTOM_DESIGN_API_BASE,
  createLocalizedError,
  eventTypeTranslationKeys,
  localizedErrorMessage,
  privateFileUrl,
  processTranslationKeys,
  projectStatusTranslationKeys,
  quantityTranslationKeys,
  quoteStatusTranslationKeys,
  quoteTypeTranslationKeys,
  readApiError,
  serviceTypeTranslationKeys,
  timelineTranslationKeys,
  translateDesignValue,
  versionStatusTranslationKeys,
} from "./customDesignUtils";
import "./CustomProjects.css";

const CustomProjectDetail = () => {
  const { projectId } = useParams();
  const { t, formatDate, formatNumber } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [messageFile, setMessageFile] = useState(null);
  const [review, setReview] = useState({ decision: "approve", feedback: "" });
  const [draftFiles, setDraftFiles] = useState([]);

  const formatProjectDate = (value) => formatDate(value, { dateStyle: "medium", timeStyle: "short" });
  const formatUsd = (cents) => formatNumber(Number(cents || 0) / 100, { style: "currency", currency: "USD" });

  const paymentState = new URLSearchParams(location.search).get("payment");
  const submitted = new URLSearchParams(location.search).get("submitted") === "true";

  const loadProject = useCallback(async (silent = false) => {
    if (!user) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/projects/${projectId}`, {
        credentials: "include",
      });
      if (!response.ok) throw createLocalizedError(await readApiError(response, t("design.detail.error.load")));
      const data = await response.json();
      setProject(data.project);
    } catch {
      setError(t("design.detail.error.load"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId, user, t]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    import("@google/model-viewer").catch(() => {
      // Render images and the GLB download link when the interactive viewer cannot load.
    });
  }, []);

  useEffect(() => {
    if (paymentState !== "success" || !user) return undefined;
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      loadProject(true);
      if (attempts >= 10) window.clearInterval(timer);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [paymentState, user, loadProject]);

  const pendingVersion = useMemo(
    () => project?.versions?.find((version) => version.status === "pending_review") || null,
    [project],
  );

  const updateFromResponse = async (response, fallback) => {
    if (!response.ok) throw createLocalizedError(await readApiError(response, fallback));
    const data = await response.json();
    if (data.project) setProject(data.project);
    return data;
  };

  const payQuote = async (quoteId) => {
    setBusy(`quote-${quoteId}`);
    setActionError("");
    try {
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/quotes/${quoteId}/checkout`, {
        method: "POST",
        credentials: "include",
      });
      const data = await updateFromResponse(response, t("design.detail.error.checkout"));
      if (data.url) window.location.assign(data.url);
    } catch (paymentError) {
      setActionError(localizedErrorMessage(paymentError, t("design.detail.error.checkout")));
      setBusy("");
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    setBusy("message");
    setActionError("");
    try {
      const payload = new FormData();
      payload.append("body", message);
      if (messageFile) payload.append("file", messageFile);
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/projects/${projectId}/messages`, {
        method: "POST",
        credentials: "include",
        body: payload,
      });
      await updateFromResponse(response, t("design.detail.error.message"));
      setMessage("");
      setMessageFile(null);
    } catch (messageError) {
      setActionError(localizedErrorMessage(messageError, t("design.detail.error.message")));
    } finally {
      setBusy("");
    }
  };

  const submitReview = async (event) => {
    event.preventDefault();
    if (!pendingVersion) return;
    setBusy("review");
    setActionError("");
    try {
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/versions/${pendingVersion.id}/review`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(review),
      });
      await updateFromResponse(response, t("design.detail.error.review"));
      setReview({ decision: "approve", feedback: "" });
    } catch (reviewError) {
      setActionError(localizedErrorMessage(reviewError, t("design.detail.error.review")));
    } finally {
      setBusy("");
    }
  };

  const chooseSampleStep = async (decision) => {
    setBusy(`sample-${decision}`);
    setActionError("");
    try {
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/projects/${projectId}/sample-decision`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      await updateFromResponse(response, t("design.detail.error.sample"));
    } catch (decisionError) {
      setActionError(localizedErrorMessage(decisionError, t("design.detail.error.sample")));
    } finally {
      setBusy("");
    }
  };

  const completeDraft = async () => {
    setBusy("draft");
    setActionError("");
    try {
      for (const file of draftFiles) {
        const payload = new FormData();
        payload.append("kind", "drawing");
        payload.append("files", file);
        const uploadResponse = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/projects/${projectId}/files`, {
          method: "POST",
          credentials: "include",
          body: payload,
        });
        await updateFromResponse(uploadResponse, t("design.detail.error.draftUpload"));
      }
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/projects/${projectId}/submit`, {
        method: "POST",
        credentials: "include",
      });
      await updateFromResponse(response, t("design.detail.error.draftSubmit"));
      setDraftFiles([]);
    } catch (draftError) {
      setActionError(localizedErrorMessage(draftError, t("design.detail.error.draftSubmit")));
    } finally {
      setBusy("");
    }
  };

  const deleteDraft = async () => {
    if (!window.confirm(t("design.detail.confirmDelete"))) return;
    setBusy("delete-draft");
    setActionError("");
    try {
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}/api/custom-design/projects/${projectId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw createLocalizedError(await readApiError(response, t("design.detail.error.draftDelete")));
      navigate("/account/custom-projects");
    } catch (draftError) {
      setActionError(localizedErrorMessage(draftError, t("design.detail.error.draftDelete")));
      setBusy("");
    }
  };

  if (!authLoading && !user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (loading) {
    return <main className="custom-project-detail-page"><div className="custom-projects-empty">{t("design.detail.loading")}</div></main>;
  }

  if (error || !project) {
    return (
      <main className="custom-project-detail-page">
        <Link className="custom-project-back" to="/account/custom-projects"><FaArrowLeft /> {t("design.hero.projects")}</Link>
        <div className="custom-projects-alert">{error || t("design.detail.notFound")}</div>
      </main>
    );
  }

  return (
    <main className="custom-project-detail-page">
      <Seo title={project.title} description={t("design.detail.seoDescription")} path={`/account/custom-projects/${project.id}`} noindex />
      <Link className="custom-project-back" to="/account/custom-projects"><FaArrowLeft /> {t("design.hero.projects")}</Link>

      {submitted && <div className="custom-project-banner success"><FaCheck /> {t("design.detail.submitted")}</div>}
      {paymentState === "success" && <div className="custom-project-banner"><FaRedo /> {t("design.detail.paymentSuccess")}</div>}
      {paymentState === "cancelled" && <div className="custom-project-banner warning">{t("design.detail.paymentCancelled")}</div>}
      {actionError && <div className="custom-projects-alert">{actionError}</div>}

      <header className="custom-project-detail-header">
        <div>
          <span className={`custom-project-status status-${project.status}`}>
            {translateDesignValue(t, projectStatusTranslationKeys, project.status)}
          </span>
          <h1>{project.title}</h1>
          <p>{project.description}</p>
        </div>
        <aside>
          <span>{t("design.detail.projectId")}</span><strong>{project.id}</strong>
          <span>{t("design.detail.service")}</span><strong>{translateDesignValue(t, serviceTypeTranslationKeys, project.serviceType)}</strong>
          <span>{t("design.detail.updated")}</span><strong>{formatProjectDate(project.updatedAt)}</strong>
        </aside>
      </header>

      <div className="custom-project-detail-layout">
        <div className="custom-project-main">
          {project.status === "draft" ? (
            <section className="custom-project-section custom-project-draft-actions">
              <div className="custom-project-section__heading"><div><span>{t("design.detail.draftKicker")}</span><h2>{t("design.detail.draftTitle")}</h2></div></div>
              <p>{t("design.detail.draftDescription")}</p>
              <label>
                <FaFileAlt /> {draftFiles.length ? t("design.detail.newFiles", { count: draftFiles.length }) : t("design.detail.chooseFiles")}
                <input type="file" multiple accept=".png,.jpg,.jpeg,.webp,.pdf,.step,.stp,.dxf" onChange={(event) => setDraftFiles(Array.from(event.target.files || []))} />
              </label>
              <div>
                <button type="button" onClick={completeDraft} disabled={busy === "draft"}>
                  {busy === "draft" ? t("design.detail.submitting") : t("design.detail.submitDraft")}
                </button>
                <button className="danger" type="button" onClick={deleteDraft} disabled={busy === "delete-draft"}>{t("design.detail.deleteDraft")}</button>
              </div>
            </section>
          ) : null}

          {project.quotes?.length ? (
            <section className="custom-project-section">
              <div className="custom-project-section__heading"><div><span>{t("design.detail.quotesKicker")}</span><h2>{t("design.detail.quotesTitle")}</h2></div></div>
              <div className="custom-project-quotes">
                {project.quotes.map((quote) => (
                  <article className={`custom-project-quote quote-${quote.status}`} key={quote.id}>
                    <div className="custom-project-quote__heading">
                      <div><span>{translateDesignValue(t, quoteTypeTranslationKeys, quote.quoteType)}</span><h3>{formatUsd(quote.totalCents)}</h3></div>
                      <em>{translateDesignValue(t, quoteStatusTranslationKeys, quote.status)}</em>
                    </div>
                    <ul>
                      {quote.items.map((item) => (
                        <li key={item.id}><span>{item.description} × {item.quantity}</span><strong>{formatUsd(item.unitAmountCents * item.quantity)}</strong></li>
                      ))}
                    </ul>
                    {quote.notes && <p>{quote.notes}</p>}
                    <div className="custom-project-quote__meta">
                      {quote.leadTime && <span>{t("design.detail.leadTime", { leadTime: quote.leadTime })}</span>}
                      {quote.validUntil && <span>{t("design.detail.validUntil", { date: formatProjectDate(quote.validUntil) })}</span>}
                    </div>
                    {["published", "checkout_started"].includes(quote.status) ? (
                      <button type="button" onClick={() => payQuote(quote.id)} disabled={Boolean(busy)}>
                        {busy === `quote-${quote.id}` ? t("design.detail.openingCheckout") : t("design.detail.pay", { amount: formatUsd(quote.totalCents) })}
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {project.versions?.length ? (
            <section className="custom-project-section">
              <div className="custom-project-section__heading"><div><span>{t("design.detail.designKicker")}</span><h2>{t("design.detail.designTitle")}</h2></div></div>
              <div className="custom-design-versions">
                {project.versions.map((version) => (
                  <article className="custom-design-version" key={version.id}>
                    <div className="custom-design-version__heading">
                      <div><span>{t("design.detail.version", { number: version.versionNumber })}</span><h3>{version.title}</h3></div>
                      <em>{translateDesignValue(t, versionStatusTranslationKeys, version.status)}</em>
                    </div>
                    {version.glbFile ? (
                      <model-viewer
                        src={privateFileUrl(version.glbFile)}
                        poster={privateFileUrl(version.renders?.[0]) || undefined}
                        alt={t("design.detail.modelAlt", { projectTitle: project.title, number: version.versionNumber })}
                        camera-controls
                        touch-action="pan-y"
                        shadow-intensity="1"
                        exposure="1"
                        with-credentials
                      />
                    ) : null}
                    {version.renders?.length ? (
                      <div className="custom-design-renders">
                        {version.renders.map((render) => <a href={privateFileUrl(render)} target="_blank" rel="noreferrer" key={render.id}><img src={privateFileUrl(render)} alt={t("design.detail.renderAlt", { versionTitle: version.title })} /></a>)}
                      </div>
                    ) : null}
                    <div className="custom-design-version__notes">
                      <p>{version.notes || t("design.detail.noNotes")}</p>
                      {version.glbFile && <a href={privateFileUrl(version.glbFile)}><FaDownload /> {t("design.detail.downloadGlb")}</a>}
                    </div>
                    {version.customerFeedback && <blockquote>{version.customerFeedback}</blockquote>}
                  </article>
                ))}
              </div>
              {pendingVersion ? (
                <form className="custom-design-review-form" onSubmit={submitReview}>
                  <h3>{t("design.detail.reviewTitle", { number: pendingVersion.versionNumber })}</h3>
                  <div>
                    <label><input type="radio" checked={review.decision === "approve"} onChange={() => setReview((current) => ({ ...current, decision: "approve" }))} /> {t("design.detail.approve")}</label>
                    <label><input type="radio" checked={review.decision === "request_changes"} onChange={() => setReview((current) => ({ ...current, decision: "request_changes" }))} /> {t("design.detail.requestChanges")}</label>
                  </div>
                  <textarea value={review.feedback} onChange={(event) => setReview((current) => ({ ...current, feedback: event.target.value }))} placeholder={review.decision === "approve" ? t("design.detail.approvalPlaceholder") : t("design.detail.changesPlaceholder")} required={review.decision === "request_changes"} />
                  <button type="submit" disabled={Boolean(busy)}>{busy === "review" ? t("design.detail.submitting") : t("design.detail.submitReview")}</button>
                </form>
              ) : null}
            </section>
          ) : null}

          <section className="custom-project-section">
            <div className="custom-project-section__heading"><div><span>{t("design.detail.messagesKicker")}</span><h2>{t("design.detail.messagesTitle")}</h2></div><FaComment /></div>
            <div className="custom-project-messages">
              {project.messages?.length ? project.messages.map((item) => (
                <article className={`custom-project-message from-${item.authorRole}`} key={item.id}>
                  <header><strong>{item.authorRole === "admin" ? t("design.detail.designTeam") : item.authorName || t("design.detail.you")}</strong><span>{formatProjectDate(item.createdAt)}</span></header>
                  <p>{item.body}</p>
                  {item.attachments?.map((file) => <a href={privateFileUrl(file)} target="_blank" rel="noreferrer" key={file.id}><FaFileAlt /> {file.originalName}</a>)}
                </article>
              )) : <p className="custom-project-empty-copy">{t("design.detail.messagesEmpty")}</p>}
            </div>
            {project.status !== "cancelled" && project.status !== "draft" ? (
              <form className="custom-project-message-form" onSubmit={sendMessage}>
                <textarea value={message} maxLength="3000" onChange={(event) => setMessage(event.target.value)} placeholder={t("design.detail.messagePlaceholder")} required />
                <div>
                  <label><FaFileAlt /> {messageFile ? messageFile.name : t("design.detail.attach")}<input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.step,.stp,.dxf" onChange={(event) => setMessageFile(event.target.files?.[0] || null)} /></label>
                  <button type="submit" disabled={busy === "message"}>{busy === "message" ? t("design.detail.sending") : t("design.detail.send")} <FaPaperPlane /></button>
                </div>
              </form>
            ) : null}
          </section>

          {["prototype_shipped", "sample_review"].includes(project.status) ? (
            <section className="custom-project-section custom-sample-decision">
              <div className="custom-project-section__heading"><div><span>{t("design.detail.sampleKicker")}</span><h2>{t("design.detail.sampleTitle")}</h2></div></div>
              <p>{t("design.detail.sampleDescription")}</p>
              <div>
                <button type="button" onClick={() => chooseSampleStep("finish")} disabled={Boolean(busy)}>{t("design.detail.finishSample")}</button>
                <button type="button" onClick={() => chooseSampleStep("small_batch")} disabled={Boolean(busy)}>{t("design.detail.smallBatch")}</button>
                <button type="button" onClick={() => chooseSampleStep("mold_production")} disabled={Boolean(busy)}>{t("design.detail.moldProduction")}</button>
              </div>
            </section>
          ) : null}
        </div>

        <aside className="custom-project-sidebar">
          <section>
            <h2>{t("design.detail.projectDetails")}</h2>
            <dl>
              <div><dt>{t("design.detail.dimensions")}</dt><dd>{project.dimensions || t("design.detail.notSpecified")}</dd></div>
              <div><dt>{t("design.detail.quantity")}</dt><dd>{project.quantity ? translateDesignValue(t, quantityTranslationKeys, project.quantity) : t("design.detail.notSpecified")}</dd></div>
              <div><dt>{t("design.detail.material")}</dt><dd>{project.material || t("design.detail.recommendation")}</dd></div>
              <div><dt>{t("design.detail.process")}</dt><dd>{translateDesignValue(t, processTranslationKeys, project.preferredProcess || "not_sure")}</dd></div>
              <div><dt>{t("design.detail.timeline")}</dt><dd>{translateDesignValue(t, timelineTranslationKeys, project.targetTimeline || "flexible")}</dd></div>
            </dl>
          </section>

          <section>
            <h2>{t("design.detail.sourceFiles")}</h2>
            <div className="custom-project-files">
              {project.files?.map((file) => (
                <a href={privateFileUrl(file)} target="_blank" rel="noreferrer" key={file.id}>
                  <FaFileAlt /><span><strong>{file.originalName}</strong><small>{formatNumber(file.sizeBytes / 1024 / 1024, { maximumFractionDigits: 2 })} MB</small></span><FaExternalLinkAlt />
                </a>
              ))}
            </div>
          </section>

          {project.trackingNumber ? (
            <section className="custom-project-shipping">
              <FaShippingFast />
              <h2>{t("design.detail.shipment")}</h2>
              <span>{project.trackingCarrier}</span><strong>{project.trackingNumber}</strong>
              {project.trackingUrl && <a href={project.trackingUrl} target="_blank" rel="noreferrer">{t("design.detail.track")} <FaExternalLinkAlt /></a>}
            </section>
          ) : null}

          <section>
            <h2>{t("design.detail.timelineTitle")}</h2>
            <ol className="custom-project-timeline">
              {[...(project.events || [])].reverse().map((event) => (
                <li key={event.id}><span /><div><strong>{translateDesignValue(t, eventTypeTranslationKeys, event.eventType)}</strong><small>{formatProjectDate(event.createdAt)}</small></div></li>
              ))}
            </ol>
          </section>
        </aside>
      </div>
    </main>
  );
};

export default CustomProjectDetail;
